'use client'

/**
 * Assistant access field
 *
 * Assign who can use an Assistant: only you, specific people,
 * the whole workspace, or public — with a calm, intentional control.
 */

import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  IconCheck,
  IconGlobe,
  IconLock,
  IconSearch,
  IconUserPlus,
  IconUsers,
  IconX
} from '@tabler/icons-react'
import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import type { AssistantSharing } from '@/lib/assistants/access'
import { cn } from '@/lib/utils'

export type AssistantAccessPerson = {
  userId: string
  displayName: string | null
  username: string | null
  email: string | null
  imageUrl: string | null
}

type WorkspaceMentionMember = {
  userId: string
  displayName: string | null
  username: string | null
  email: string | null
  imageUrl: string | null
}

type AccessOption = {
  value: AssistantSharing
  title: string
  description: string
  icon: typeof IconLock
}

const OPTIONS: AccessOption[] = [
  {
    value: 'private',
    title: 'Only you',
    description: 'Stays in your sidebar. No one else can open it.',
    icon: IconLock
  },
  {
    value: 'shared',
    title: 'People',
    description: 'Invite specific teammates from this workspace.',
    icon: IconUserPlus
  },
  {
    value: 'workspace',
    title: 'Workspace',
    description: 'Everyone in this workspace can use it.',
    icon: IconUsers
  },
  {
    value: 'public',
    title: 'Public',
    description: 'Signed-in Losi users can use it with the link.',
    icon: IconGlobe
  }
]

/** True when create/save is allowed for the current access draft. */
export function isAssistantAccessReady(
  sharing: AssistantSharing,
  memberUserIds: string[]
): boolean {
  if (sharing !== 'shared') return true
  return memberUserIds.length > 0
}

function personLabel(person: {
  displayName?: string | null
  username?: string | null
  email?: string | null
}) {
  return (
    person.displayName?.trim() ||
    person.username?.trim() ||
    person.email?.trim() ||
    'Teammate'
  )
}

function initials(label: string) {
  const parts = label.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase()
  }
  return label.slice(0, 2).toUpperCase() || '?'
}

export interface AssistantAccessFieldProps {
  value: AssistantSharing
  onChange: (value: AssistantSharing) => void
  memberUserIds: string[]
  onMemberUserIdsChange: (userIds: string[]) => void
  workspaceId?: string | null
  workspaceName?: string | null
  /** When set, load existing members for edit surfaces */
  assistantId?: string | null
  currentUserId?: string | null
  disabled?: boolean
  className?: string
}

export const AssistantAccessField: FC<AssistantAccessFieldProps> = ({
  value,
  onChange,
  memberUserIds,
  onMemberUserIdsChange,
  workspaceId,
  workspaceName,
  assistantId,
  currentUserId,
  disabled = false,
  className
}) => {
  const [directory, setDirectory] = useState<AssistantAccessPerson[]>([])
  const [knownPeople, setKnownPeople] = useState<
    Record<string, AssistantAccessPerson>
  >({})
  const [query, setQuery] = useState('')
  const [isLoadingDirectory, setIsLoadingDirectory] = useState(false)
  const [isLoadingAccess, setIsLoadingAccess] = useState(false)

  const selectedPeople = useMemo(() => {
    return memberUserIds
      .map((userId) => {
        return (
          knownPeople[userId] ||
          directory.find((person) => person.userId === userId) || {
            userId,
            displayName: null,
            username: null,
            email: null,
            imageUrl: null
          }
        )
      })
      .filter((person) => person.userId !== currentUserId)
  }, [memberUserIds, knownPeople, directory, currentUserId])

  const mergeKnown = useCallback((people: AssistantAccessPerson[]) => {
    setKnownPeople((prev) => {
      const next = { ...prev }
      for (const person of people) {
        next[person.userId] = person
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (!assistantId) return

    let cancelled = false
    const load = async () => {
      setIsLoadingAccess(true)
      try {
        const res = await authenticatedFetch(
          `/api/assistants/${assistantId}/access`
        )
        const data = await res.json().catch(() => null)
        if (!res.ok || cancelled) return

        const sharing = (data?.sharing || 'private') as AssistantSharing
        onChange(sharing)

        const members = Array.isArray(data?.members) ? data.members : []
        const people: AssistantAccessPerson[] = members.map((member: any) => ({
          userId: member.userId || member.user_id,
          displayName: member.displayName ?? member.display_name ?? null,
          username: member.username ?? null,
          email: member.email ?? null,
          imageUrl: member.imageUrl ?? member.image_url ?? null
        }))
        mergeKnown(people)
        onMemberUserIdsChange(
          people
            .map((person) => person.userId)
            .filter((id) => id && id !== currentUserId)
        )
      } catch {
        // Non-blocking — create flow and local state still work.
      } finally {
        if (!cancelled) setIsLoadingAccess(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
    // Intentionally only re-fetch when assistant changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantId])

  useEffect(() => {
    if (value !== 'shared' || !workspaceId) return

    let cancelled = false
    const loadDirectory = async () => {
      setIsLoadingDirectory(true)
      try {
        const res = await authenticatedFetch(
          `/api/workspaces/${workspaceId}/mention-members`
        )
        const data = await res.json().catch(() => null)
        if (!res.ok || cancelled) {
          if (!res.ok && !cancelled) {
            toast.error('Couldn’t load workspace people')
          }
          return
        }

        const members: AssistantAccessPerson[] = (
          (data?.members || []) as WorkspaceMentionMember[]
        )
          .filter((member) => member.userId !== currentUserId)
          .map((member) => ({
            userId: member.userId,
            displayName: member.displayName,
            username: member.username,
            email: member.email,
            imageUrl: member.imageUrl
          }))

        setDirectory(members)
        mergeKnown(members)
      } catch {
        if (!cancelled) toast.error('Couldn’t load workspace people')
      } finally {
        if (!cancelled) setIsLoadingDirectory(false)
      }
    }

    void loadDirectory()
    return () => {
      cancelled = true
    }
  }, [value, workspaceId, currentUserId, mergeKnown])

  const filteredDirectory = useMemo(() => {
    const selected = new Set(memberUserIds)
    const q = query.trim().toLowerCase()
    return directory
      .filter((person) => !selected.has(person.userId))
      .filter((person) => {
        if (!q) return true
        const haystack = [
          person.displayName,
          person.username,
          person.email
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(q)
      })
      .slice(0, 8)
  }, [directory, memberUserIds, query])

  const handleSelect = (next: AssistantSharing) => {
    if (disabled) return
    if (next === 'shared' && !workspaceId) return
    onChange(next)
  }

  const addPerson = (person: AssistantAccessPerson) => {
    if (disabled) return
    mergeKnown([person])
    if (!memberUserIds.includes(person.userId)) {
      onMemberUserIdsChange([...memberUserIds, person.userId])
    }
    setQuery('')
  }

  const removePerson = (userId: string) => {
    if (disabled) return
    onMemberUserIdsChange(memberUserIds.filter((id) => id !== userId))
  }

  return (
    <div className={cn('space-y-3 pt-3', className)}>
      <div className="space-y-0.5">
        <Label className="text-sm font-medium tracking-tight">Who can use this</Label>
      </div>

      <div
        role="radiogroup"
        aria-label="Assistant access"
        className="grid gap-2"
      >
        {OPTIONS.map((option) => {
          const selected = value === option.value
          const Icon = option.icon
          const peopleLocked = option.value === 'shared' && !workspaceId
          const description =
            option.value === 'workspace'
              ? workspaceName?.trim()
                ? `Everyone in ${workspaceName.trim()} can use it.`
                : option.description
              : peopleLocked
                ? 'Select a workspace first to invite people.'
                : option.description

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled || peopleLocked}
              onClick={() => handleSelect(option.value)}
              className={cn(
                'group relative flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
                'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                selected
                  ? 'border-foreground/20 bg-foreground/[0.03] shadow-[0_1px_0_rgba(0,0,0,0.04)]'
                  : 'border-border/70 bg-background hover:border-foreground/15 hover:bg-muted/40',
                (disabled || peopleLocked) && 'cursor-not-allowed opacity-60'
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors',
                  selected
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground group-hover:text-foreground'
                )}
              >
                <Icon size={16} stroke={1.75} />
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-[13.5px] font-medium tracking-tight">
                    {option.title}
                  </span>
                  {selected && (
                    <IconCheck
                      size={14}
                      className="text-foreground/70"
                      stroke={2}
                    />
                  )}
                </span>
                <span className="text-muted-foreground mt-0.5 block text-[12.5px] leading-snug">
                  {description}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      {value === 'shared' && (
        <div className="border-border/70 bg-muted/20 space-y-3 rounded-xl border p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[13px] font-medium tracking-tight">People with access</p>
            <p className="text-muted-foreground text-[11.5px]">
              {selectedPeople.length
                ? `${selectedPeople.length} selected`
                : 'None yet'}
            </p>
          </div>

          {selectedPeople.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedPeople.map((person) => {
                const label = personLabel(person)
                return (
                  <span
                    key={person.userId}
                    className="bg-background border-border/80 inline-flex max-w-full items-center gap-1.5 rounded-full border py-1 pl-1 pr-2"
                  >
                    <Avatar className="size-5">
                      {person.imageUrl ? (
                        <AvatarImage src={person.imageUrl} alt="" />
                      ) : null}
                      <AvatarFallback className="text-[9px]">
                        {initials(label)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate text-[12px] font-medium">
                      {label}
                    </span>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => removePerson(person.userId)}
                      className="text-muted-foreground hover:text-foreground rounded-full p-0.5"
                      aria-label={`Remove ${label}`}
                    >
                      <IconX size={12} />
                    </button>
                  </span>
                )
              })}
            </div>
          )}

          <div className="relative">
            <IconSearch
              size={14}
              className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search teammates…"
              aria-label="Search teammates"
              disabled={disabled || isLoadingDirectory || !workspaceId}
              className="h-9 border-border/70 bg-background pl-8 text-[13px]"
            />
          </div>

          <div className="max-h-44 space-y-0.5 overflow-y-auto" aria-live="polite">
            {!workspaceId ? (
              <p className="text-muted-foreground px-1 py-2 text-[12.5px]">
                Choose a workspace, then invite teammates here — or use Only you.
              </p>
            ) : isLoadingDirectory || isLoadingAccess ? (
              <p className="text-muted-foreground px-1 py-2 text-[12.5px]">
                Loading people…
              </p>
            ) : filteredDirectory.length === 0 ? (
              <p className="text-muted-foreground px-1 py-2 text-[12.5px]">
                {query.trim()
                  ? 'No matching teammates.'
                  : directory.length
                    ? 'Everyone available is already added.'
                    : 'No other teammates in this workspace yet. Invite someone to the workspace, or choose Only you / Workspace.'}
              </p>
            ) : (
              filteredDirectory.map((person) => {
                const label = personLabel(person)
                return (
                  <button
                    key={person.userId}
                    type="button"
                    disabled={disabled}
                    onClick={() => addPerson(person)}
                    className="hover:bg-background flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left transition-colors"
                  >
                    <Avatar className="size-7">
                      {person.imageUrl ? (
                        <AvatarImage src={person.imageUrl} alt="" />
                      ) : null}
                      <AvatarFallback className="text-[10px]">
                        {initials(label)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium">
                        {label}
                      </span>
                      {person.email && (
                        <span className="text-muted-foreground block truncate text-[11.5px]">
                          {person.email}
                        </span>
                      )}
                    </span>
                    <IconUserPlus
                      size={14}
                      className="text-muted-foreground shrink-0"
                    />
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}

      {value === 'shared' && selectedPeople.length === 0 && (
        <p
          className="text-amber-700/90 dark:text-amber-300/90 px-0.5 text-[12px]"
          role="status"
        >
          Add at least one person before saving.
        </p>
      )}
    </div>
  )
}
